import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransformMatrix } from '@mds/common';
import { escape } from 'lodash';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { AnalyzeService } from '@services/analyze.service';
import { EventDelegatorService } from '@services/event-delegator.service';
import {
  FilterAndBreadcrumbEventName,
  FilterAndBreadcrumbEventsService,
  FilterData
} from '@services/filtersAndBreadcrumb.service';
import { GlobalDataService } from '@services/global-data.service';
import { ToastrService } from '@services/toaster.service';
import * as d3 from 'd3';
import _ from 'lodash';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { saveSvgAsPng } from 'save-svg-as-png';
import { GraphLegendV2 } from './network-graph-v2-legend';

@Component({
  selector: 'app-network-graph-v2',
  standalone: true,
  imports: [CommonModule, MdsIconsModule],
  templateUrl: './network-graph-v2.component.html',
  styleUrls: ['./network-graph-v2.component.scss']
})
export class NetworkGraphV2Component implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() data;
  @ViewChild('graph') networkGraph: ElementRef;
  @ViewChild('customMenuList') customMenuList!: ElementRef;
  icons = MdsIcon;

  productName: string;
  layer: string;
  graphData: any;
  hiddenIds: any[] = [];
  menuDataSet: any[] = [];
  currentClickNode: any;
  showEditOriginal = false;
  ngxEventBus: any[] = [];
  isSimulationsLessThan50 = true;
  @Input() defaultView: string;
  @Input() svgResize: string;
  private windowData: any = window;
  private tierCount = 0;
  private legend: GraphLegendV2;
  private transformMatrix: TransformMatrix;
  private storedElement!: HTMLElement;
  private previousClickedRid: string;

  private filterEvents = inject(FilterAndBreadcrumbEventsService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private globalDataService: GlobalDataService,
    private toastrService: ToastrService,
    private eventDelegatorService: EventDelegatorService,
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    private analyzeService: AnalyzeService
  ) {}
  ngOnChanges() {
    if (this.defaultView) {
      this.init();
      d3.select('#graph').selectAll('*').remove();
    }
    if (this.svgResize) {
      this.resizeGraph();
    }
    if (this.data) {
      this.init();
    }
  }

  ngOnInit() {
    this.filterEvents.filterDataChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: FilterData) => {
      if (data.eventType === FilterAndBreadcrumbEventName.BACK_TO_DEFAULT) {
        d3.select('#graph').selectAll('*').remove();
        this.init();
      }
    });
  }

  ngAfterViewInit(): void {
    const resizeSubscription = fromEvent(window, 'resize')
      .pipe(debounceTime(100))
      .subscribe(() => this.resizeGraph());
    this.ngxEventBus.push(resizeSubscription);
    this.storedElement = this.customMenuList.nativeElement;
    this.cd.detectChanges();
    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        this.createData(this.data);
      });
    }, 800);
  }

  ngOnDestroy() {
    if (this.networkGraph && this.networkGraph.nativeElement?.children.length > 0) {
      this.networkGraph.nativeElement.children[0]?.remove();
    }
    this.ngxEventBus.forEach((e) => e?.unsubscribe());
  }

  init() {
    if (this.networkGraph && this.networkGraph.nativeElement?.children.length > 0) {
      this.networkGraph.nativeElement.children[0]?.remove();
    }
    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        this.createData(this.data);
      });
    }, 300);
  }

  menuEvent(value) {
    if (value === 'savepng') {
      this.toastrService.showMessage('Generating png file of sankey graph', 2000);
      this.saveSvg();
    } else {
      this.eventDelegatorService.emitter('custom-ngx-event', {
        type: 'graph-event',
        action: value,
        data: this.currentClickNode,
        controller: window.location.pathname
      });
    }
    setTimeout(() => {
      const menulist = document.getElementById('customMenuList');
      menulist.style.visibility = 'hidden';
    }, 300);
  }

  saveSvg() {
    const clonedDiv = document.querySelector('#graphSvg');
    const g = clonedDiv.querySelector('g');
    clonedDiv.setAttribute('width', `${+g.getAttribute('width') + 1200}`);
    clonedDiv.setAttribute('height', `${+g.getAttribute('height') + 1800}`);
    saveSvgAsPng(clonedDiv, 'Sankey_Graph.png', {
      scale: 1,
      backgroundColor: '#FFFFFF',
      top: -300,
      left: -300
    });
  }

  createData(json) {
    this.graphData = _.cloneDeep(json.data);
    this.hiddenIds = [];

    if (!this.graphData.nodes) return;
    const nodes = [];
    const nodeIds = [];
    const leafNodes = [];
    const links = [];

    // create nodes data
    this.graphData.nodes.forEach((d) => {
      leafNodes.push(d);
      d.visible = true;
      const child = [];
      // if name is grater than 14 chars then truncate with three dot(...)
      d.shortName = d.name.length > 14 ? `${d.name.slice(0, 14)}...` : d.name;
      // find child nodes for - d
      this.graphData.nodes.forEach((n) => {
        if (d.id === n.parents[n.parents.length - 2]) {
          child.push(n);
        }
      });
      d.child = child;
      if (this.tierCount < d.Tier) this.tierCount = d.Tier;
      nodes.push(d);
      nodeIds.push(d.id);
    });

    this.graphData.nodes = nodes;

    // create links
    this.graphData.links.forEach((link) => {
      const s = nodes.filter((a) => a.id === link.source);
      link.targetId = link.target;
      link.sourceId = link.source;

      if (s && s.length > 0) {
        link.source = s[0];
      }

      const t = nodes.filter((a) => a.id === link.target);
      if (t && t.length > 0) {
        link.target = t[0];
      }

      link.sourceTier = link.source.Tier;
      links.push(link);
    });

    this.graphData.links = links;
    this.drawGraph(this.graphData);
  }

  getFilteredNode(graph): any[] {
    return graph.nodes.filter((e) => {
      let isAvailable = true;
      if (this.hiddenIds.includes(e.id)) {
        // one of the parents node is hidden then node itself is also hidden
        // fix for chain break issue
        const p = [...e.parents];
        p.pop();
        for (const i in p) {
          if (this.hiddenIds.includes(p[i])) {
            isAvailable = false;
            break;
          }
        }
      } else {
        // check all parents is visible
        for (const i in e.parents) {
          if (this.hiddenIds.includes(e.parents[i])) {
            isAvailable = false;
            break;
          }
        }
      }
      return isAvailable;
    });
  }

  getFilteredLinks(graph): any[] {
    return graph.links.filter((e) => {
      let isAvailable = true;
      const { target } = e;
      // if target node of link is available then show link, otherwise link hidden
      if (this.hiddenIds.includes(target.id)) {
        isAvailable = false;
      } else {
        const parent = target.parents;
        // one of the parent node of target node is hidden then then link is hidden
        for (const i in parent) {
          if (this.hiddenIds.includes(parent[i])) {
            isAvailable = false;
            break;
          }
        }
      }
      return isAvailable;
    });
  }

  drawGraph(graph) {
    NgZone.assertNotInAngularZone();

    const self = this;
    const legendData = graph.node_legend || [];

    const currNodes = self.getFilteredNode(graph);
    const currLinks = self.getFilteredLinks(graph);
    const { width, height } = this.analyzeService.getViewportSize('graph');

    let fadeTimeOut;
    const linkedByIndex = {};
    currLinks.forEach((d) => {
      linkedByIndex[`${d.source.id},${d.target.id}`] = 1;
    });

    d3.select(this.networkGraph.nativeElement).select('svg').remove();

    // create svg start
    const svg = d3
      .select(this.networkGraph.nativeElement)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('id', 'graphSvg')
      .attr('transform', 'translate(0,0) scale(1)');

    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 2.5]) // Set min and max zoom limits
      .on('zoom', zoomFun); // Attach event listener
    d3.select(this.networkGraph.nativeElement).call(zoom).on('dblclick.zoom', null);

    const child = svg
      .append('g')
      .attr('width', () => {
        const windowWidth = window.innerWidth - 5;
        return windowWidth;
      })
      .attr('height', () => {
        const windowHeight = window.innerHeight - 70;
        return windowHeight;
      })
      .attr('transform', 'translate(0, 0) scale(1)');
    // arrow at the end show from here
    svg
      .append('svg:defs')
      .selectAll('marker')
      .data(['end']) // Different link/path types can be defined here
      .enter()
      .append('svg:marker') // This section adds in the arrows
      .attr('id', String)
      .attr('viewBox', '0 -5 15 15')
      .attr('refX', 32)
      .attr('refY', 0)
      .attr('markerWidth', 10)
      .attr('markerHeight', 10)
      .style('fill', '#3181bd')
      .attr('orient', 'auto')
      .append('svg:path')
      .attr('d', 'M0,-5L10,0L0,5');
    // create svg -- end

    // created background filter for text - to avoid title overlap
    svg
      .append('defs')
      .append('filter')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 1)
      .attr('height', 1)
      .attr('id', 'nodeTitleTextBg')
      .append('feFlood')
      .attr('flood-color', '#FFFFFF')
      .append('feComposite')
      .attr('in', 'SourceGraphic')
      .attr('operator', 'xor');
    this.legend = new GraphLegendV2(svg, legendData); // add legends on graph

    // draw graph -- start
    const simulation = d3
      .forceSimulation()
      .nodes(currNodes)
      .force(
        'link',
        d3
          .forceLink(currLinks)
          .strength(2)
          .distance(100)
          .id((d: any) => d.id)
      )
      .force('charge', d3.forceManyBody().distanceMax(height).strength(-1200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .velocityDecay(0.2)
      .alphaDecay(0.07)
      .on('end', () => {
        svg.attr('simulation-end', true);
      });

    // links in graph
    const link = child
      .append('g')
      .attr('class', 'links')
      .attr('stroke', '#d3d3d3')
      .selectAll('line')
      .data(currLinks)
      .enter()
      .append('line');

    // link code end

    // draw nodes - start
    const node = child
      .selectAll('.node')
      .data(currNodes)
      .enter()
      .append('g')

      .attr('fixed', (d) => (d.fixed = false))
      .attr('class', 'nodes')
      .style('cursor', 'move')
      .call(drag(simulation))
      .on('dblclick', (ev, d) => {
        if (d.child.length > 0) {
          d.visible = !d.visible;
          if (!d.visible) {
            self.hiddenIds.push(d.id);
          } else {
            const idIndex = self.hiddenIds.indexOf(d.id);
            if (idIndex !== -1) {
              self.hiddenIds.splice(idIndex, 1);
            }
          }
          redrawGraph();
        }
      })
      .on('mouseover.fade', fade(0.1))
      .on('mouseout.fade', fadeOut())
      .on('click', (d, event: any) => {
        self.currentClickNode = event;
        const data: any = {};
        data.isFrom = 'mattermaps';
        data.module = 'mattermaps';
        data.layer = self.data.ppmLayer;
        if (this.previousClickedRid !== data.rid || this.previousClickedRid === undefined) {
          this.previousClickedRid = data.rid?.replace('#', '');

          highlightNodeLinksPrestep(data, event);
        }
      })
      .on('contextmenu', (event, node) => {
        self.currentClickNode = node;
        self.menuDataSet = [];
        setMenuDataset(node);
        menuPosition(100, event, 'visible', this.storedElement);
        self.cd.detectChanges();
      });

    const pinicon = node
      .append('image')
      .attr('xlink:href', (d: any) => {
        if (d.node_pins && d.node_pins.length > 0) {
          return '/assets/icons/svg-imports/round-push-pin.svg';
        }
        return '';
      })
      .attr('x', -8)
      .attr('y', -31)
      .attr('width', 16)
      .attr('height', 16);

    const pinTooltip = d3.select('body').append('div').attr('class', 'tooltip').style('opacity', 0);

    let pinTooltipTimeout;
    pinicon
      .on('mouseover', (event: any, d: any) => {
        if (d.node_pins && d.node_pins.length > 0) {
          pinTooltipTimeout = setTimeout(() => {
            pinTooltip.style('opacity', 1);
            pinTooltip.style('position', 'absolute');
            pinTooltip.style('visibility', 'visible');
            pinTooltip.style('z-index', 100);
            pinTooltip.style('pointer-events', 'none');

            const pinTooltipDiv = document.createElement('div');
            const str = `<div flex='100' layout='column' layout-align='space-around center' style='padding-left: 5px; padding-right:5px;'><div style='font-size:14px;padding-right:6px;'>${escape(d.node_pins[0].label)}</div> <div style='font-size:14px;margin: 6px 0px;'>${escape(d.node_pins[0].value)}</div> </div>`;
            pinTooltipDiv.innerHTML = `<div style='min-width:200px;max-width:450px;color:#FFFFFF; background-color:#4c4c4c;border-radius:4px;min-height:50px;'>${str}</div>`;
            pinTooltip.html(pinTooltipDiv.innerHTML);
            pinTooltip.style('left', `${event.pageX + 15}px`);
            pinTooltip.style('top', `${event.pageY - 20}px`);
          }, 200);
          return true;
        }
        return false;
      })
      .on('mouseout', () => {
        pinTooltip.style('opacity', 0);
        if (pinTooltipTimeout) {
          clearTimeout(pinTooltipTimeout);
        }
      });

    // node circle with backgroud color
    node
      .append('circle')
      .attr('r', 15)
      .attr('class', 'nodes-circle')
      .style('pointer-events', 'visible')
      .style('stroke-width', 20)
      .style('stroke-opacity', (d) => {
        // d.visible -- false then shows shadow araound the node
        if (!d.visible) {
          return '.1';
        }
        return '0';
      })
      .style('stroke', (d) => {
        if (!d.visible) {
          return 'blue';
        }
        return '';
      })
      .attr('fill', (d) => {
        // inside node
        if (d.id === 0) {
          return '#9c27b0';
        }
        if (d.has_alternatives) {
          for (const legend of legendData) {
            if (legend.type === 'has_alternatives') {
              return legend.color;
            }
          }
        } else if (
          d.rid &&
          d.rid_original &&
          d.rid.replace('#', '') !== d.rid_original.replace('#', '') &&
          d.node_type !== 'commodity'
        ) {
          return '#A52A2A';
        } else if (d.node_type === 'commodity') {
          return '#6C66D1';
        } else {
          for (const legend of legendData) {
            if (d.type === legend.type) {
              return legend.color;
            }
          }
        }
      });

    // node image
    node
      .append('image')
      .attr('xlink:href', (d) => {
        let typeimg;
        if (d.type === 'product' && d.type_label === 'Product Group') {
          typeimg = 'product-group';
        } else if (['regulation', 'Regulation', 'enhesa', 'Enhesa'].includes(d.type)) {
          typeimg = 'ecofee';
        } else {
          typeimg = d.type;
        }
        return `/assets/icons/composer-data-objects/${typeimg}-inverse.svg`;
      })
      .attr('class', 'nodes-img')
      .attr('x', -8)
      .attr('y', -8)
      .attr('width', 16)
      .attr('height', 16);

    /**
     * node title
     * - if title length more than 14 then it should be truncated
     * - hover on it show full text
     */
    let hoverOnTextTimeOut;
    const group = node.append('g').on('mouseover', textExpand).on('mouseout', textCollapsed);

    // this is background for text- it should expand and collapsed same as text on hover
    const textBg = group
      .append('text')
      .text((d) => d.shortName)
      .attr('x', 24)
      .attr('dy', '.35em')
      .style('opacity', 0.8)
      .style('filter', 'url(#nodeTitleTextBg)');

    // this is text that shows on node
    const text = group
      .append('text')
      .attr('x', 24)
      .attr('dy', '.35em')
      .attr('class', 'nodes-text')
      .style('fill', '#54575d')
      .style('opacity', 0.8)
      .attr('font-family', 'sans-serif')
      .attr('font-size', 12)
      .style('cursor', 'pointer')
      .text((d) => d.shortName);
    // title text code --end

    simulation.nodes(currNodes).on('tick', tick);
    this.legend.setPosition(this.networkGraph);
    // nodes code -- end
    function highlightNodeLinksPrestep(node, data) {
      const eventData = {
        type: 'sankey',
        action: 'showInfoNode',
        data,
        controller: window.location.pathname
      };
      self.globalDataService.nodeEvent = eventData;
    }
    // // drag nodes code
    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.1).restart();
        svg.on('.zoom', null);
      }

      function dragged(event, d) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
        fixNodes(d);
      }

      function dragended(event) {
        simulation.stop();
        if (!event.active) simulation.alphaTarget(0);
        svg.call(zoom);
      }

      return d3.drag().on('start', dragstarted).on('drag', dragged).on('end', dragended);
    }

    function fixNodes(thisNode) {
      node.each((d) => {
        if (thisNode !== d) {
          d.fx = d.x;
          d.fy = d.y;
        }
      });
    }

    function tick() {
      // link between 2 nodes
      if (self.isSimulationsLessThan50) checkCompletionStatus();

      link
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);
      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    }

    /** *
     * checking simulation completion percentage
     */
    function checkCompletionStatus() {
      const completionPercentage = (1 - simulation.alpha()) * 100; // alpha value start from 1, indicating simulation has progressed towards reaching a stable state (0 is stable).
      if (completionPercentage > 50) {
        self.zone.run(() => {
          self.graphSimulationDone();
        });
      }
    }

    /**
     *
     * this function expand text and remove three dot from text - hover on text
     */
    function textExpand(ev, d) {
      ev.stopPropagation();
      if (hoverOnTextTimeOut) {
        window.clearTimeout(hoverOnTextTimeOut);
      }
      hoverOnTextTimeOut = setTimeout(() => {
        text.text((o) => (o.id === d.id ? o.name : o.shortName));
        textBg.text((o) => (o.id === d.id ? o.name : o.shortName));
      }, 500);
    }
    function textCollapsed(ev) {
      ev.stopPropagation();
      if (hoverOnTextTimeOut) {
        window.clearTimeout(hoverOnTextTimeOut);
      }

      text.text((o) => o.shortName);
      textBg.text((o) => o.shortName);
    }

    // remove highlight for nodes and links on hover
    function fadeOut() {
      return (e, d) => {
        if (fadeTimeOut) {
          window.clearTimeout(fadeTimeOut);
        }
        node.style('stroke-opacity', function (o) {
          this.setAttribute('fill-opacity', '1');
          return 1;
        });
        link.style('stroke-opacity', 1);
        link.attr('marker-end', 'url(#end)');
        text.text((o) => o.shortName);
        text.style('opacity', 0.8);
        textBg.text((o) => o.shortName);
        textBg.style('opacity', 0.8);
      };
    }

    // add highlight efect on node and links on hovering
    function fade(opacity) {
      return (e, d) => {
        window.clearTimeout(fadeTimeOut);
        fadeTimeOut = undefined;
        fadeTimeOut = setTimeout(() => {
          node.style('stroke-opacity', function (o) {
            const thisOpacity = d.parents.includes(o.id) || o.id === 0 ? 1 : opacity;
            this.setAttribute('fill-opacity', thisOpacity);
            return thisOpacity;
          });
          link.style('stroke-opacity', (o) => (d.parents.includes(o.source.id) ? 1 : opacity));
          link.attr('marker-end', (o) => (d.parents.includes(o.source.id) ? 'url(#end)' : ''));
          text.text((o) => (d.parents.includes(o.id) || o.id === 0 ? o.name : o.shortName));
          text.style('opacity', (o) => (d.parents.includes(o.id) || o.id === 0 ? 1 : opacity));
          textBg.text((o) => (d.parents.includes(o.id) || o.id === 0 ? o.name : o.shortName));
          textBg.style('opacity', (o) => (d.parents.includes(o.id) || o.id === 0 ? 1 : 0));
        }, 1000);
      };
    }

    function isDrillable(node) {
      if (node.type === 'process' || (node.type === 'product' && node.in_process)) {
        return true;
      }
      return false;
    }

    function setMenuDataset(node) {
      const featureArray = self.globalDataService.getFeatureArray();
      if (featureArray.indexOf('mattermaps') !== -1) {
        self.menuDataSet.push('savepng');
        if (isDrillable(node)) {
          self.menuDataSet.push('navigate');
        }
      }

      if (featureArray.indexOf('composer_process') !== -1) {
        self.menuDataSet.push('edit');
      }

      if (featureArray.indexOf('todos') !== -1) {
        self.menuDataSet.push('todo');
      }
      if (['product', 'process', 'company'].includes(node.type)) {
        self.menuDataSet.push('substitute');
      }

      self.showEditOriginal = false;
      if (node.rid && node.rid_original && node.rid.replace('#', '') !== node.rid_original.replace('#', '')) {
        self.showEditOriginal = true;
      }
    }

    function menuPosition(zIndex, ev, visibility, element) {
      ev.preventDefault();
      const menulist = element;
      if (menulist) {
        menulist.style.left = `${ev.pageX}px`;
        menulist.style.top = `${ev.pageY - 150}px`;
        menulist.style.visibility = visibility;
        menulist.style.zIndex = zIndex;
      }
      d3.select('body').on('click', () => {
        menuPosition(-1, ev, 'hidden', element);
      });
    }

    function redrawGraph() {
      d3.select('#graph').selectAll('*').remove();
      self.drawGraph(self.graphData);
    }

    /**
     * @param event - d3 zoom in and zoom out event
     * limits the zoom in / zoom out to 2.5x and 0.5x
     * mouse scroll also moves the position of the graph, resets the position coordinates to previous position
     */
    function zoomFun(event) {
      if (!event?.transform) return; // Early exit if event or transform is missing

      const { k, x, y } = event.transform; // Destructure for readability

      // Store the new transform state
      self.transformMatrix = { k, x, y };

      // Update font size dynamically based on zoom level
      node.selectAll('text').style('font-size', `${(k * 2 + 10) / k}px`);

      // Apply transformation
      child.attr('transform', `translate(${x}, ${y}) scale(${k})`);

      // Emit event only for specific zoom levels
      if (![0.8, 1].includes(k)) {
        self.eventDelegatorService.emitter('custom-ngx-event', {
          type: 'graph-event',
          action: 'zoom',
          controller: window.location.pathname
        });
      }
    }
  }

  private resizeGraph() {
    setTimeout(() => {
      const { width, height } = this.analyzeService.getViewportSize('graph');
      d3.select('#graph svg')?.attr('width', width).attr('height', height);
      this.legend?.setPosition(this.networkGraph);
    }, 500);
  }

  graphSimulationDone() {
    this.isSimulationsLessThan50 = false;
  }
}
